import { keepPreviousData, QueryClient } from '@tanstack/react-query';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { client } from '@/api/client.gen';
import { AUTH_MODE, AuthMode, ENV } from '@/config/env';
import { MSAL_SCOPES, pca } from '@/config/msal';
import { buildApiErrorFromClientError, buildApiErrorFromResponse, getErrorStatus, isClientError } from '@/lib/api-error';
import { clearTokens } from '@/lib/auth-token';
import { ensureFreshToken } from '@/lib/token-refresh';
import { router } from '@/router';
import { useErrorDialogStore } from '@/stores/error-dialog-store';

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			placeholderData: keepPreviousData,
			refetchOnWindowFocus: false,
			// 4xx won't succeed on retry and each attempt would re-open the error dialog.
			retry: (failureCount, error) => {
				const status = getErrorStatus(error);

				if (status !== null && isClientError(status)) {
					return false;
				}

				return failureCount < 3;
			},
		},
	},
});

const isRealAuth = AUTH_MODE === AuthMode.Real;

async function getAuthTokenForDemo(): Promise<string> {
	const account = pca.getActiveAccount();

	if (!account) {
		throw new Error('No active account found');
	}

	try {
		const token = await pca.acquireTokenSilent({ account, scopes: MSAL_SCOPES });

		return token.accessToken;
	} catch (error) {
		await pca.clearCache();
		void router.navigate({ to: '/sign-in' });

		console.error('Failed to acquire token:', error);
		throw new Error('Failed to acquire authentication token');
	}
}

async function redirectToSignIn(): Promise<void> {
	queryClient.clear();

	if (isRealAuth) {
		clearTokens();
	} else {
		try {
			await pca.clearCache();
		} catch {
			// Cache may already be gone; navigating to sign-in is what matters.
		}
	}

	void router.navigate({ to: '/sign-in' });
}

function isBackgroundSagaPoll(request: Request | undefined): boolean {
	return request?.method === 'GET' && request.url.includes('/api/Saga/');
}

async function getToken(): Promise<string | undefined> {
	if (isRealAuth) {
		return (await ensureFreshToken()) ?? undefined;
	}

	return getAuthTokenForDemo();
}

client.setConfig({
	baseUrl: ENV.VITE_API_BASE_URL,
	auth: () => getToken(),
});

client.interceptors.error.use(async (error, response, request, options) => {
	if (response?.status === 401) {
		await redirectToSignIn();

		return error;
	}

	// Aborted requests (query cancelled on unmount/navigation) are not failures.
	if (!response && (request?.signal.aborted || (error instanceof DOMException && error.name === 'AbortError'))) {
		return error;
	}

	// Saga polling runs in the background and reports failures itself (see waitForSaga).
	if (isBackgroundSagaPoll(request)) {
		return error;
	}

	const requestInfo = {
		url: response?.url ?? request?.url ?? options.url,
		// `options.body` is the original (unserialized) request body.
		payload: options.body,
	};

	// No response: the request failed client-side (token, serialization, fetch itself) — surface the real error.
	if (!response) {
		console.error('API request failed before receiving a response:', error);
		useErrorDialogStore.getState().showError(buildApiErrorFromClientError(error, requestInfo));

		return error;
	}

	useErrorDialogStore
		.getState()
		.showError(buildApiErrorFromResponse(error, { status: response.status, statusText: response.statusText }, requestInfo));

	return error;
});

axios.defaults.baseURL = ENV.VITE_API_BASE_URL;

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

axios.interceptors.request.use(
	async (config) => {
		try {
			const token = await getToken();

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		} catch (error) {
			console.error('Failed to add auth token to request:', error);
			throw error;
		}

		return config;
	},
	(error) => {
		return Promise.reject(error as Error);
	},
);

axios.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const config = error.config as RetriableConfig | undefined;

		if (error?.response?.status === 401) {
			// Real auth: try to refresh once, then retry the original request.
			if (isRealAuth && !config?._retried) {
				const token = await ensureFreshToken();

				if (token && config) {
					config._retried = true;
					config.headers.Authorization = `Bearer ${token}`;

					return axios(config);
				}
			}

			await redirectToSignIn();

			// Reject so callers never treat an auth failure as a successful empty response.
			return Promise.reject(error);
		}

		useErrorDialogStore.getState().showError(error);

		return Promise.reject(error);
	},
);
